<template>
  <v-col>
    <v-card>
      <v-card-title primary-title>
        <v-row v-if="reportType === 'Membership Summary'">
          <v-col lg="8" sm="12">{{ this.reportType }}</v-col>
          <v-col cols="2">
            <div class="subtitle-2 float-right">
              <p class="my-0">T = Total Membership</p>
              <p class="my-0 primary--text">E = Enrollments</p>
              <p class="my-0 secondary--text">D = Disenrollments</p>
            </div>
          </v-col>
          <!-- <v-col lg="2" sm="12">
            <div class="float-right">
              <v-btn color="secondary" text small @click="expandAll">Expand All</v-btn>
              <v-btn color="primary" text small @click="collapseAll">Collapse All</v-btn>
            </div>
          </v-col> -->
        </v-row>
        <v-row v-else>
          <v-col lg="8" sm="12">{{ this.reportType }}</v-col>
          <v-col lg="4" sm="12">
            <div class="float-right">
              <v-btn color="secondary" text small @click="expandAll">Expand All</v-btn>
              <v-btn color="primary" text small @click="collapseAll">Collapse All</v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" class="pt-0 mt-2">
            <v-text-field
              class="pt-0"
              v-model="search"
              label="Search"
              append-icon="mdi-search"
              single-line
              hide-details
            ></v-text-field>
          </v-col>
        </v-row>
      </v-card-text>

      <!-- All membership reports except for Membership Summary -->
      <v-data-table
        :headers="headers"
        :items="tableData"
        :search="search"
        :sort-by="[sortColumn()]"
        :sort-desc="[true]"
        :footer-props="{
          'items-per-page-options': [25, 50, 75, 100, -1],
        }"
        :items-per-page="25"
        @update:pagination="getMore()"
        multi-sort
        :show-group-by="reportType != 'Membership Summary'"
        class="elevation-0 col-md-12 m-auto"
      >
        <template
          v-if="reportType === 'Membership Summary'"
          v-for="header in headers"
          v-slot:[`item.${header.value}`]="{ item }"
        >
          <div v-if="header.value.toLowerCase() !== 'name' && header.value !== 'Member Average'">
            <tr>
              T:
              {{
                item[header.value] ? item[header.value].membership : '0'
              }}
            </tr>
            <tr class="primary--text">
              E:
              {{
                item[header.value] ? item[header.value].enrolls : '0'
              }}
            </tr>
            <tr class="secondary--text">
              D:
              {{
                item[header.value] ? item[header.value].disenrolls : '0'
              }}
            </tr>
          </div>

          <v-chip
            v-else-if="header.value === 'Member Average'"
            :color="getColor(item[header.value], item, header.value)"
          >
            {{ item[header.value] }}
          </v-chip>

          <div v-else>
            {{ item[header.value] }}
          </div>
        </template>

        <template v-slot:group.header="{ group, headers, isOpen, toggle, remove }">
          <td style="background: #fff" :colspan="headers.length">
            <v-btn @click="toggle" small icon :ref="group" :data-open="isOpen">
              <v-icon v-if="isOpen">mdi-chevron-up</v-icon>
              <v-icon v-else>mdi-chevron-down</v-icon>
            </v-btn>
            <span class="font-weight-bold">{{ group }}</span>
            <v-icon small @click="remove">mdi-close</v-icon>
          </td>
        </template>

        <template v-slot:item.DETAILS="{ item }">
          <patient-modal :patient="item" :insurance="insurance" />
        </template>

        <template v-slot:item.AGE="{ item }">
          {{ calcAge(item.DOB) }}
        </template>

        <template v-slot:item.AMOUNT="{ item }">
          {{ formatCurrency(item.AMOUNT) }}
        </template>

        <template v-if="reportType === 'Patients Not Seen'" v-slot:item.ID="{ item }">
          <v-chip small @click="patientDetails(item.ID)">{{ item.ID }}</v-chip>
        </template>

        <template v-slot:item.LAST_SERVICE="{ item }">
          {{ item.LAST_SERVICE ? item.LAST_SERVICE : '-' }}
        </template>

        <template
          v-if="reportType === 'Membership Summary' && loading === false"
          v-slot:body.append="props"
        >
          <tr>
            <td v-for="(header, index) in headers" :key="index">
              <div v-if="header.value == 'name'">Monthly Averages</div>
              <div v-else>
                <v-chip :color="getColor(averageObject[header.value], averageObject, header.value)">
                  {{ averageObject[header.value] }}
                </v-chip>
              </div>
            </td>
          </tr>
        </template>

        <template slot="body.append" slot-scope="props" v-if="reportType === 'Capitation'">
          <!-- renders with categories for grouped reports -->
          <tr>
            <td :colspan="props.headers.length" class="primary--text font-weight-medium">
              <span>Claims Displayed: {{ props.items.length }}</span>
              <span class="float-right font-weight-bold">
                Displayed Capitation Amount:
                {{ formatCurrency(props.items.reduce((a, rec) => a + rec.AMOUNT, 0)) }}
              </span>
              <span style="display: inline" class="float-right mr-9">
                Total Capitation Amount:
                {{ formatCurrency(reportData.reduce((a, rec) => a + rec.AMOUNT, 0)) }}
              </span>
            </td>
          </tr>
        </template>

        <template slot="footer">
          <ExportCSV :data="this.csvData" :name="this.reportType + ' ' + this.startDate + '.csv'">
            <v-btn color="secondary" text small class="export-button">Export to CSV</v-btn>
          </ExportCSV>
        </template>
      </v-data-table>

      <highCostPatientDetails
        :insurance="insurance"
        :startDate="startDate"
        :endDate="endDate"
        :patientID="patientID"
        :reportType="reportType"
        v-model="patientDialog"
      />
    </v-card>
  </v-col>
</template>

<script>
import ExportCSV from 'vue-json-csv';
import PatientModal from '../PatientModal.vue';
import highCostPatientDetails from '../utilizationComponents/highCostPatientDetails.vue';

export default {
  name: 'MembershipReports',
  props: ['reportData', 'csvData', 'reportType', 'insurance', 'loading', 'startDate', 'endDate'],
  components: {
    PatientModal,
    highCostPatientDetails,
    ExportCSV,
  },
  data() {
    return {
      search: '',
      user: '',
      pagination: '',
      headers: [],
      tableData: [],
      averageObject: {},
      patientID: '',
      patientDialog: false,
    };
  },
  mounted() {
    console.log(this.reportData);
  },
  methods: {
    getHeaders() {
      const reportType = this.reportType ? this.reportType.toLowerCase() : null;
      const insurance = this.insurance ? this.insurance : null;
      if (reportType === null || insurance === null) {
        return [];
      }
      if (reportType === 'membership roster') {
        this.headers = [
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          {
            text: 'Provider/Center',
            align: 'left',
            value: 'PCP_NAME',
          },
          {
            text: 'Subscriber ID',
            align: 'left',
            value: 'SUBSCRIBER_ID',
          },
          // { text: 'DOB', value: 'DOB' },
          {
            text: 'Address',
            align: 'left',
            value: 'ADDRESS',
          },
          { text: 'Effective Date', value: 'PCP_EFFECTIVE_DATE' },
          { text: 'Term Date', value: 'PCP_TERM_DATE' },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else if (reportType === 'new members') {
        this.headers = [
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          {
            text: 'Provider/Center',
            align: 'left',
            value: 'PCP_NAME',
          },
          {
            text: 'Subscriber ID',
            align: 'left',
            value: 'SUBSCRIBER_ID',
          },
          // { text: 'DOB', value: 'DOB' },
          {
            text: 'Address',
            align: 'left',
            value: 'ADDRESS',
          },
          { text: 'Risk Score C', value: 'RISKSCORE_C' },
          { text: 'Risk Score D', value: 'RISKSCORE_D' },
          { text: 'Effective Date', value: 'PCP_EFFECTIVE_DATE' },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else if (reportType === 'disenrollments') {
        this.headers = [
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          {
            text: 'Provider Name',
            align: 'left',
            value: 'PCP_NAME',
          },
          {
            text: 'Subscriber ID',
            align: 'left',
            value: 'SUBSCRIBER_ID',
          },
          // { text: 'DOB', value: 'DOB' },
          {
            text: 'Address',
            align: 'left',
            value: 'ADDRESS',
          },
          { text: 'PCP Term Date', value: 'PCP_TERM_DATE' },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else if (reportType === 'membership summary') {
        this.headers = [];
        const newArr = [];
        for (let i = 0; i < this.reportData.length; i++) {
          Object.keys(this.reportData[i]).forEach((d) => {
            if (newArr.indexOf(d) === -1) {
              newArr.push(d);
            }
          });
        }
        newArr.forEach((key) => {
          if (key !== 'name' && key !== 'Member Average') {
            this.headers.push({ text: key, value: key, width: '125px' });
          }
          // this.headers.push({ text: key, value: key, width: '125px' });
        });
        this.headers.unshift({ text: 'name', value: 'name', width: '125px' });
        this.headers.push({ text: 'Member Average', value: 'Member Average', width: '125px' });
      } else if (reportType === 'patients not seen') {
        this.headers = [
          { text: 'Patient ID', value: 'ID' },
          { text: 'Patient Name', value: 'PATIENT_NAME' },
          { text: 'Last Visit', value: 'LAST_SERVICE' },
          { text: 'Provider Name', value: 'PROVIDER_NAME' },
          // { text: 'DOB', value: 'DOB' },
          { text: 'Amount', value: 'AMOUNT' },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else if (reportType === 'ens panel') {
        this.headers = [
          { text: 'MRN', value: 'MRN' },
          { text: 'first_name', value: 'FIRST_NAME' },
          { text: 'middle_name', value: 'MIDDLE_NAME' },
          { text: 'last_name', value: 'LAST_NAME' },
          { text: 'address_line_1', value: 'ADDR1' },
          { text: 'address_line_2', value: 'ADDR2' },
          { text: 'city', value: 'CITY' },
          { text: 'state', value: 'STATE' },
          { text: 'zip', value: 'ZIP' },
          { text: 'date_of_birth', value: 'DOB' },
          { text: 'gender', value: 'GENDER' },
          { text: 'ssn', value: 'SSN' },
          { text: 'ins_id', value: 'PATIENT_INS_ID' },
          { text: 'home_phone', value: 'HOME_PHONE' },
          { text: 'work_phone', value: 'WORK_PHONE' },
          { text: 'cell_phone', value: 'CELL_PHONE' },
          { text: 'Affiliate', value: 'AFFILIATE' },
          { text: 'CenterNumber', value: 'CENTER_NUM' },
          { text: 'CenterName', value: 'CENTER_NAME' },
          { text: 'HALOS_Insurance', value: 'HALOS_INS' },
          { text: 'MBI', value: 'MBI' },
          { text: 'ContactEmail', value: 'EMAIL1' },
          { text: 'ContactEmail2', value: 'EMAIL2' },
        ];
      } else if (reportType === 'capitation') {
        this.headers = [
          { text: 'Member ID', value: 'MEMBER_ID' },
          { text: 'Member Name', value: 'MEMBER_NAME' },
          { text: 'Provider Name', value: 'PROVIDER_NAME' },
          { text: 'Cap Date', value: 'CAP_DATE' },
          { text: 'Amount', value: 'AMOUNT' },
        ];
      } else if (reportType === 'open conditions') {
        this.headers = [
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          {
            text: 'Member ID',
            align: 'left',
            value: 'ID',
          },
          {
            text: 'Phone Number',
            align: 'left',
            value: 'PHONE',
          },
          {
            text: 'Provider Name',
            align: 'left',
            value: 'PCP_NAME',
          },
          {
            text: 'Disease Condition',
            align: 'left',
            value: 'DISEASE_CONDITION',
          },
          {
            text: 'COC',
            align: 'left',
            value: 'COC',
          },
          { text: 'Details', value: 'DETAILS' },
        ];
      } else if (reportType === 'open tasks') {
        this.headers = [
          { text: 'Patient Name', value: 'MEMBER_NAME' },
          {
            text: 'Member ID',
            align: 'left',
            value: 'ID',
          },
          {
            text: 'Phone Number',
            align: 'left',
            value: 'PHONE',
          },
          {
            text: 'Provider Name',
            align: 'left',
            value: 'PCP_NAME',
          },
          {
            text: 'Result',
            align: 'left',
            value: 'TASK_RESULT',
          },
          {
            text: 'Objetive',
            align: 'left',
            value: 'TASK_OBJECTIVE',
          },
          { text: 'Details', value: 'DETAILS' },
        ];
      }
    },
    sortColumn() {
      const reportType = this.reportType ? this.reportType.toLowerCase() : null;
      const insurance = this.insurance ? this.insurance : null;
      if (reportType === null || insurance === null) {
        return 'AMOUNT';
      } if (reportType === 'disenrollments') {
        return 'DISENROLLMENT_DATE';
      } if (reportType === 'membership roster' || reportType === 'new members') {
        return 'ELIGIBLE_MONTH';
      } if (reportType === 'patients not seen' || reportType === 'capitation') {
        return 'AMOUNT';
      }
    },
    async patientDetails(ID) {
      this.patientID = ID;
      this.patientDialog = true;
    },
    collapseAll() {
      Object.keys(this.$refs).forEach((k) => {
        if (this.$refs[k] && this.$refs[k].$attrs['data-open']) {
          this.$refs[k].$el.click();
        }
      });
    },
    expandAll() {
      Object.keys(this.$refs).forEach((k) => {
        if (this.$refs[k] && !this.$refs[k].$attrs['data-open']) {
          this.$refs[k].$el.click();
        }
      });
    },
    getColor(value, item, headerValue) {
      const newArr = [];
      const values = [];
      let currentMonth;

      Object.keys(item).forEach((key) => {
        newArr.push({ [key]: item[key] });
        values.push(item[key]);
      });
      for (let i = 0; i < newArr.length; i++) {
        if (newArr[i].hasOwnProperty(headerValue)) {
          currentMonth = newArr[i];
        }
      }

      const curIndex = newArr.findIndex(el => el == currentMonth);

      const curVal = values[curIndex];
      const prevVal = values[curIndex - 1];

      if (curVal > prevVal) {
        return 'green lighten-1';
      }
      return 'red lighten-1 white--text';
    },
    calcAge(birthday) {
      const today = new Date();
      const birthDate = new Date(birthday);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    formatCurrency(c) {
      if (c != undefined) return `$${c.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
    },
  },
  watch: {
    reportData() {
      this.search = '';
      this.getHeaders();
      this.tableData = this.reportData;
    },
    csvData() {
      this.csvData.forEach((item) => {
        for (const [key, value] of Object.entries(item)) {
          if (typeof value === 'object') {
            item[key] = JSON.stringify(item[key]);
            item[key] = item[key].replaceAll(/([}{])/g, '');
            item[key] = item[key].replaceAll(':', ': ');
            item[key] = item[key].replace(',', ', ');
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.export-button {
  position: absolute;
  left: 10px;
  bottom: 16px;
}
</style>
